import type { AgentRunRow, EventTypeRow, Json } from '../../lib/database.types'
import { at, num } from '../../lib/json'

/**
 * The event-type registry: what every `events.type` means and how to say it.
 *
 * `public.event_types` is published from 98h-agents `config/event-types.json`
 * by the box, like `fleet_components` -- read-only here, and never computed or
 * inferred here. Each row carries a plain label, a sentence template, a
 * category and whether it is a milestone. This module turns that into three
 * things the feed needs: which rows are story, how to filter for them on the
 * server, and the sentence to show.
 *
 * A type the registry does not know still renders, as its own summary, exactly
 * as it did before the registry existed. An unregistered name is something to
 * notice, not something to hide.
 */

export interface EventTypeDef {
  type: string
  label: string
  template: string
  category: string
  isMilestone: boolean
  replaces: string[]
  status: string
}

export interface Registry {
  byName: ReadonlyMap<string, EventTypeDef>
  defs: readonly EventTypeDef[]
}

export const EMPTY_REGISTRY: Registry = { byName: new Map(), defs: [] }

export function toRegistry(rows: ReadonlyArray<EventTypeRow>): Registry {
  const defs = rows.map<EventTypeDef>((r) => ({
    type: r.type,
    label: r.label,
    template: r.template,
    category: r.category,
    isMilestone: r.is_milestone,
    replaces: r.replaces ?? [],
    status: r.status,
  }))
  // Canonical names only. `replaces` is kept for traceability, not resolved:
  // since the backfill of 24 Sep 2026 no row carries a legacy name, and
  // events.type is foreign-keyed to this table, so none can.
  return { byName: new Map(defs.map((d) => [d.type, d])), defs }
}

export type Resolution = { kind: 'event'; def: EventTypeDef } | { kind: 'unregistered' }

/**
 * The type's definition, or unregistered. Heartbeats are not events -- they are
 * a table of their own (public.heartbeats) -- so there is nothing to tell apart
 * here but a known type from one the registry has not published yet.
 */
export function resolve(registry: Registry, type: string): Resolution {
  const def = registry.byName.get(type)
  return def ? { kind: 'event', def } : { kind: 'unregistered' }
}

/*
 * -- Which rows a view shows --------------------------------------------------
 *
 * story       what happened to a piece of work, a gate or a finding. Leaves out
 *             the raw Linear deliveries: integration rows that are not
 *             milestones, which are webhook echo of the ticket the run already
 *             narrates. Heartbeats never reach this table at all.
 * milestones  only the key beats (is_milestone).
 * all         everything the table holds, for someone debugging the plumbing.
 */
export type Lens = 'story' | 'milestones' | 'all'

export function inLens(res: Resolution, lens: Lens): boolean {
  if (lens === 'all') return true
  if (res.kind === 'unregistered') return lens === 'story'
  if (lens === 'milestones') return res.def.isMilestone
  return !isEcho(res.def)
}

function isEcho(def: EventTypeDef): boolean {
  return def.category === 'integration' && !def.isMilestone
}

function namesOf(defs: readonly EventTypeDef[]): string[] {
  return defs.map((d) => d.type)
}

/** A PostgREST list: quoted, because a type name is a string the fleet chooses. */
function list(names: readonly string[]): string {
  return `(${names.map((n) => `"${n}"`).join(',')})`
}

/**
 * The server-side filter for a lens, as a PostgREST `or` expression, or null
 * for no filter.
 *
 * Server-side because the feed is capped before a row reaches the browser, so
 * a filter applied afterwards cannot reach past what the cap already spent.
 * That was DEV-36's heartbeat problem; heartbeats have their own table now,
 * but raw Linear deliveries still arrive faster than the story does.
 *
 * With an empty registry -- before its first publish -- there is nothing to
 * select milestones by or echo to leave out, so both lenses show everything
 * rather than an empty page.
 */
export function lensFilter(registry: Registry, lens: Lens): string | null {
  if (lens === 'all' || registry.defs.length === 0) return null
  if (lens === 'milestones') {
    return `type.in.${list(namesOf(registry.defs.filter((d) => d.isMilestone)))}`
  }
  const echo = namesOf(registry.defs.filter(isEcho))
  return echo.length ? `type.not.in.${list(echo)}` : null
}

/*
 * -- The sentence -------------------------------------------------------------
 */

const PLACEHOLDER = /\{([^{}]+)\}/g

/** Every payload path the registry's templates use, e.g. ['review','verdict']. */
export function payloadPaths(registry: Registry): string[][] {
  const seen = new Set<string>()
  for (const d of registry.defs) {
    for (const m of d.template.matchAll(PLACEHOLDER)) {
      const name = m[1]!
      if (name.startsWith('payload.')) seen.add(name.slice('payload.'.length))
    }
  }
  return [...seen].sort().map((p) => p.split('.'))
}

type RunFields = Pick<AgentRunRow, 'external_ref' | 'title' | 'repo' | 'agent' | 'attempt' | 'cost_usd' | 'pr_url'>

function runValue(run: RunFields | null | undefined, field: string): string | null {
  if (!run) return null
  switch (field) {
    case 'external_ref': return run.external_ref || null
    case 'title': return run.title || null
    case 'repo': return run.repo || null
    case 'agent': return run.agent || null
    case 'attempt': return run.attempt === null || run.attempt === undefined ? null : String(run.attempt)
    // The template supplies the "$". Two places, because a run's cost is a
    // figure people compare, and a rounded "$3" hides a tripling from "$1.2".
    case 'cost_usd': return run.cost_usd === null || run.cost_usd === undefined ? null : run.cost_usd.toFixed(2)
    case 'pr_url': return run.pr_url || null
    default: return null
  }
}

function payloadValue(payload: Json | undefined, path: string[]): string | null {
  const v = at(payload, ...path)
  if (typeof v === 'string') return v.trim() ? v : null
  const n = num(v)
  return n === null ? null : String(n)
}

/**
 * The template, filled -- or "{label}: {summary}" the moment any placeholder
 * cannot be resolved. Half a sentence with a hole in it reads as a fact; the
 * fallback reads as what it is.
 */
export function renderEvent(
  def: EventTypeDef,
  event: { summary: string | null; payload?: Json },
  run?: RunFields | null,
): string {
  const fallback = event.summary ? `${def.label}: ${event.summary}` : def.label
  let missing = false
  const text = def.template.replace(PLACEHOLDER, (_, name: string) => {
    let value: string | null = null
    if (name === 'label') value = def.label
    else if (name === 'summary') value = event.summary || null
    else if (name.startsWith('run.')) value = runValue(run, name.slice(4))
    else if (name.startsWith('payload.')) value = payloadValue(event.payload, name.slice(8).split('.'))
    if (value === null) missing = true
    return value ?? ''
  })
  return missing ? fallback : text
}
